/**
 * confidenceBand.js — Phase 5 Intelligence Layer Projection type.
 *
 * North Star invariant #4: every projection has a confidence band.
 * "Naked point estimate" is a bug — this module defines the canonical
 * shape that every projection MUST return and provides helpers to
 * construct / validate it.
 *
 * Design:
 *   - A Projection is ALWAYS well-shaped, even for empty / insufficient-
 *     data cases. In that case, point/low/high are null and confidence
 *     is 'insufficient_data'. Callers never have to guard for "is this
 *     an error or a projection?" — same shape, nulls mean no estimate.
 *
 *   - All exported projection functions return this shape. A single
 *     invariant test (src/test/phase5-invariant.test.js) iterates the
 *     exports and asserts the shape is present on every output.
 *
 *   - This file has NO dependency on DB, Supabase, or React. Pure JS.
 */

// Minimum weeks-of-history required to declare data sufficient for a band.
// Below this, returns 'insufficient_data'. 0.5 = half a week. Phase 11 slot 1
// (Invariant #16).
const MIN_WEEKS_OF_HISTORY = 0.5;

/**
 * @typedef {Object} Projection
 * @property {number|null} point     Point estimate (e.g., 10.5 weeks). null = no estimate
 * @property {number|null} low       80%-band lower bound. null = no band
 * @property {number|null} high      80%-band upper bound. null = no band
 * @property {'high'|'medium'|'low'|'insufficient_data'} confidence
 *           Categorical confidence — 'high' = ≥ min annotators + min weeks,
 *           'medium' = partial data, 'low' = very sparse but >0 samples,
 *           'insufficient_data' = cannot estimate at all
 * @property {'gaussian'|'percentile'|'deterministic'|'none'} method
 *           Statistical method used. 'deterministic' = inputs are known
 *           (no variance); 'none' = insufficient_data case
 * @property {number} sampleSize     Number of observations used
 * @property {string} unit            e.g., 'weeks', 'items/week', 'USD'
 * @property {Date} computedAt        When the projection was computed (for staleness UX)
 * @property {string} [reason]        Optional human-readable reason when
 *                                    insufficient_data or degraded confidence
 */

/**
 * Confidence tier classification. Drives UX affordances (don't show the
 * band as a solid line if confidence is low; show a warning for
 * insufficient_data).
 *
 * Audit fix (R2 HIGH-3 + LOW-1): ALL four thresholds are required config
 * keys — high and medium tiers are symmetric in their contract. A missing
 * key throws, so a caller that forgot to wire the config can't silently
 * downgrade every projection from 'high' to 'medium' (misrepresenting
 * confidence is the worst class of bug for a probabilistic forecast).
 *
 * @param {number} annotatorCount — distinct annotators in the historical window
 * @param {number} weeksOfHistory — weeks of observations
 * @param {object} config — MUST include all four PROJECTION_MIN_* keys
 * @returns {'high'|'medium'|'low'|'insufficient_data'}
 * @throws if any required threshold is missing / non-finite
 */
export function classifyConfidence(annotatorCount, weeksOfHistory, config) {
  const minAnnHigh = config?.PROJECTION_MIN_ANNOTATORS_FOR_HIGH_CONF;
  const minWeeksHigh = config?.PROJECTION_MIN_WEEKS_FOR_HIGH_CONF;
  const minAnnMed = config?.PROJECTION_MIN_ANNOTATORS_FOR_MEDIUM_CONF;
  const minWeeksMed = config?.PROJECTION_MIN_WEEKS_FOR_MEDIUM_CONF;
  // R2 HIGH-3: fail loud on misconfigured calls rather than silently
  // producing a downgraded confidence tier.
  for (const [name, val] of [
    ['PROJECTION_MIN_ANNOTATORS_FOR_HIGH_CONF', minAnnHigh],
    ['PROJECTION_MIN_WEEKS_FOR_HIGH_CONF', minWeeksHigh],
    ['PROJECTION_MIN_ANNOTATORS_FOR_MEDIUM_CONF', minAnnMed],
    ['PROJECTION_MIN_WEEKS_FOR_MEDIUM_CONF', minWeeksMed],
  ]) {
    if (!Number.isFinite(val) || val <= 0) {
      throw new Error(`classifyConfidence: config.${name} must be a positive finite number (got ${val})`);
    }
  }
  if (!annotatorCount || !weeksOfHistory || annotatorCount < 1 || weeksOfHistory < MIN_WEEKS_OF_HISTORY) {
    return 'insufficient_data';
  }
  if (annotatorCount >= minAnnHigh && weeksOfHistory >= minWeeksHigh) return 'high';
  if (annotatorCount >= minAnnMed && weeksOfHistory >= minWeeksMed) return 'medium';
  return 'low';
}

/**
 * Build a deterministic (no-variance) projection. Use when the point
 * estimate is exact — e.g., a cost that's hours × rate where both are
 * known without error.
 *
 * R2 HIGH-1 audit fix: reject non-finite (Infinity/NaN) and negative
 * values. The rest of the projection layer relies on non-negativity
 * invariants (truncate clamps at 0) — allowing `deterministic(-5, 'USD')`
 * would slip a negative cost through validateProjection cleanly, so we
 * close that escape hatch here at the constructor.
 *
 * @param {number|null} value — null ⇒ insufficient_data; must be finite & ≥ 0 otherwise
 * @param {string} unit
 * @param {Date} [now] — R2 LOW-5: caller can inject time for test stability
 * @returns {Projection}
 * @throws if value is non-finite or negative (null is still allowed as "no data")
 */
export function deterministic(value, unit, now = new Date()) {
  if (value != null) {
    if (!Number.isFinite(value)) {
      throw new Error(`deterministic: value must be finite (got ${value})`);
    }
    if (value < 0) {
      throw new Error(`deterministic: value must be non-negative (got ${value})`);
    }
  }
  return {
    point: value,
    low: value,
    high: value,
    confidence: value == null ? 'insufficient_data' : 'high',
    method: 'deterministic',
    sampleSize: 1,
    unit,
    computedAt: now,
  };
}

/**
 * Build an insufficient-data projection. All three bounds null,
 * confidence explicit, optional human-readable reason.
 *
 * @param {string} unit
 * @param {string} [reason]
 * @param {Date} [now] — R2 LOW-5: caller can inject time for test stability
 * @returns {Projection}
 */
export function insufficientData(unit, reason, now = new Date()) {
  return {
    point: null,
    low: null,
    high: null,
    confidence: 'insufficient_data',
    method: 'none',
    sampleSize: 0,
    unit,
    computedAt: now,
    reason: reason || 'not enough historical data',
  };
}

/**
 * Build a full projection from a statistics band (gaussian / percentile).
 *
 * @param {{point, low, high, method, sampleSize}} statsBand
 * @param {'high'|'medium'|'low'|'insufficient_data'} confidence
 * @param {string} unit
 * @param {Date} [now] — R2 LOW-5: caller can inject time for test stability
 * @returns {Projection}
 */
export function fromStatsBand(statsBand, confidence, unit, now = new Date()) {
  return {
    point: statsBand.point,
    low: statsBand.low,
    high: statsBand.high,
    confidence,
    method: statsBand.method,
    sampleSize: statsBand.sampleSize,
    unit,
    computedAt: now,
  };
}

/**
 * Validate that a value is a well-shaped Projection. Used by the
 * invariant test to enforce invariant #4 at every export boundary.
 * Throws on the first violation with a descriptive message.
 *
 * @param {*} p
 * @param {string} [label] — for error messages
 */
export function validateProjection(p, label = 'projection') {
  if (p === null || typeof p !== 'object') {
    throw new Error(`${label}: expected object, got ${typeof p}`);
  }
  const required = ['point', 'low', 'high', 'confidence', 'method', 'sampleSize', 'unit', 'computedAt'];
  for (const k of required) {
    if (!(k in p)) throw new Error(`${label}: missing required field "${k}"`);
  }
  if (!['high', 'medium', 'low', 'insufficient_data'].includes(p.confidence)) {
    throw new Error(`${label}: invalid confidence "${p.confidence}"`);
  }
  if (!['gaussian', 'percentile', 'deterministic', 'none'].includes(p.method)) {
    throw new Error(`${label}: invalid method "${p.method}"`);
  }
  if (typeof p.sampleSize !== 'number' || p.sampleSize < 0 || !Number.isFinite(p.sampleSize)) {
    throw new Error(`${label}: sampleSize must be finite non-negative number`);
  }
  if (typeof p.unit !== 'string' || p.unit.length === 0) {
    throw new Error(`${label}: unit must be non-empty string`);
  }
  if (!(p.computedAt instanceof Date) || Number.isNaN(p.computedAt.getTime())) {
    throw new Error(`${label}: computedAt must be a valid Date`);
  }
  // R2 HIGH-4 audit fix: non-null bounds MUST be finite. Previously NaN
  // and Infinity slipped through — `NaN == null` is false, so
  // null-symmetry didn't reject them; ordering was skipped because
  // `NaN <= …` is false and the guard required all three non-null. A
  // config typo (e.g., config.PROJECTION_BAND_Z missing) would produce
  // NaN halfWidth → NaN bounds → clean validation → downstream silent
  // corruption. Close the hole at the contract boundary.
  for (const k of ['point', 'low', 'high']) {
    if (p[k] != null && !Number.isFinite(p[k])) {
      throw new Error(`${label}: ${k} must be finite number or null (got ${p[k]})`);
    }
  }
  // Band null-symmetry invariant (R1 H1 — audit fix): low and high must be
  // both null together or both non-null. A half-null band (low=3, high=null)
  // represents a malformed distribution and must be rejected.
  if ((p.low == null) !== (p.high == null)) {
    throw new Error(`${label}: band null-symmetry violated (low=${p.low}, high=${p.high})`);
  }
  // Band ordering invariant — when point+low+high are all present
  if (p.point != null && p.low != null && p.high != null) {
    if (!(p.low <= p.point && p.point <= p.high)) {
      throw new Error(`${label}: band ordering violated (low=${p.low}, point=${p.point}, high=${p.high})`);
    }
  }
  // If confidence is insufficient_data, all three MUST be null
  if (p.confidence === 'insufficient_data') {
    if (p.point !== null || p.low !== null || p.high !== null) {
      throw new Error(`${label}: insufficient_data requires null point/low/high`);
    }
  }
}
